import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import colors from 'virtual:colors';
import { Button } from '@/components/ui';
import { isLensProduct, type Product, type SolutionProduct } from '@/pages/Products/content';
import { ColorModal } from './ColorModal';
import { SpecPanel } from './SpecPanel';

interface ProductRowProps {
  product: Product | SolutionProduct;
  /** Spec panel label — "Lens specifications" or "Solution specifications". */
  specsLabel: string;
  /** Spec panel cells per row — 3 for lenses, 2 for Eyedia. */
  specsColumns?: 2 | 3;
  /**
   * When true, renders a "View color range" CTA between the descriptor and
   * features list, and ensures the ColorModal is mounted regardless of whether
   * this product has its own xlsx variants. Set by the Color family section.
   */
  inColorFamily?: boolean;
  /**
   * When true, the product image is the page's LCP candidate (first row) and
   * is loaded eagerly with high fetch priority instead of lazily.
   */
  priority?: boolean;
}

/**
 * A single product entry: image-left + text-right.
 *
 * Figma (node 47:2046, large 1440px):
 *   - Outer: `flex items-start justify-between` inside the 1200 row
 *     (no explicit gap; 160px emerges from 1200 − 440 − 600)
 *   - Image wrapper: 440×248, image fills via object-cover
 *   - Text col: `flex flex-col gap-[80px] items-start py-[14px] w-[600px]`
 *     - 80px gap separates the three text-col blocks (Label, Body, Specification)
 *     - py-[14px] aligns the text col baseline with the image top
 *
 * Text-col blocks (top → bottom):
 *   1. Label — flex-col gap-[16px]
 *      a. Header (no gap):
 *         - Eyebrow "Daily" / "Monthly": Karla SemiBold 12/14, color #96938e (stone)
 *         - Name: Karla Bold 16/32 tracking -0.4px, color #3e3a36 (dusk), w-[240px]
 *      b. Description (chip line): flex gap-[12px] items-start
 *         - 5 spans (Karla Regular 16/18): "Spherical" (#20191c) • [material] (#3e3a36)
 *           • [water content] (#3e3a36); bullets in #20191c.
 *   2. Body — flex-col gap-[56px]
 *      a. Descriptor paragraph: Karla Regular 16/44, color #3e3a36
 *      b. Features — flex-col gap-[8px]:
 *         - "Features" eyebrow: Karla SemiBold 12/14, color #96938e
 *         - <ul list-disc ms-[24px]> with each feature in <span leading-[44px]>,
 *           Karla Regular 16, color #3e3a36
 *   3. Specification — the bordered SpecPanel card, 600 wide
 *
 * Responsive:
 *   Mobile (<md)  : stack image-above-text, image 60vw centered (40px above + below).
 *   md / lg       : 2-col fluid, fixed 40px gap. Image starts at 440 and shrinks
 *                   first (down to its 240 min) as the container narrows; once
 *                   image hits 240, text starts shrinking (from its 600 max).
 *                   Clamp formulas (with the 40px gap baked in):
 *                     image: clamp(240, 100% − 640, 440)
 *                     text:  clamp(0,   100% − 280, 600)
 *   xl+           : fixed 440 + 160 gap + 600 = 1200 (matches figma exactly).
 */
export function ProductRow({
  product,
  specsLabel,
  specsColumns = 3,
  inColorFamily = false,
  priority = false,
}: ProductRowProps) {
  const { t } = useTranslation();
  const isLens = isLensProduct(product);
  const hasColorVariants = colors.some((c) => c.productId === product.id);
  const [colorModalOpen, setColorModalOpen] = useState(false);
  // Either a per-product xlsx variant entry or being part of the color family
  // (set by the parent section) is enough to expose the color modal.
  const showColorModal = hasColorVariants || inColorFamily;

  const productName = product.name;
  const productImageAlt = t(`products.items.${product.id}.imageAlt`);
  const productDescription = t(`products.items.${product.id}.description`);
  const productFeatures = t(`products.items.${product.id}.features`, {
    returnObjects: true,
  }) as string[];
  const productChips = isLens
    ? (t(`products.items.${product.id}.chips`, { returnObjects: true }) as string[])
    : [];
  const scheduleLabel = isLens
    ? t(
        product.schedule === 'Daily'
          ? 'products.labels.scheduleDaily'
          : 'products.labels.scheduleMonthly',
      )
    : '';
  const translatedSpecs = product.specs.map((spec) => ({
    labelKey: spec.labelKey,
    value: t(`products.items.${product.id}.specs.${spec.labelKey}`),
  }));

  const specsCta = hasColorVariants ? (
    <div className="flex flex-col items-start gap-[22px]">
      <div className="flex flex-col text-[14px] font-normal leading-5">
        <p className="text-clearlab-steel">{t('products.labels.colorRange')}</p>
        <p className="text-clearlab-midnight">{t('products.labels.colorAvailability')}</p>
      </div>
      <Button
        variant="transparent"
        size="small"
        tone="blue"
        iconRight
        className="text-[14px]"
        onClick={(e) => {
          e.stopPropagation();
          setColorModalOpen(true);
        }}
      >
        {t('products.labels.viewColors')}
      </Button>
    </div>
  ) : undefined;
  // Image wrapper height at xl+ — defaults to 248. Width is always 440 at xl,
  // so aspectRatio derives a proportional height at smaller breakpoints.
  const imageHeight = product.imageHeight ?? 248;
  return (
    <article className="flex flex-col gap-10 md:flex-row md:items-start md:gap-10 xl:gap-40">
      <div
        className="mx-auto mt-10 w-[60vw] md:mx-0 md:mt-0 md:w-[clamp(240px,calc(100%_-_640px),440px)] md:flex-none xl:w-[440px]"
        style={{ aspectRatio: `440 / ${imageHeight}` }}
      >
        <img
          src={product.image}
          alt={productImageAlt}
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'high' : undefined}
          className="block h-full w-full object-cover"
        />
      </div>

      <div className="flex flex-col items-start md:w-[clamp(0px,calc(100%_-_280px),600px)] md:flex-none md:py-3.5 xl:w-[600px]">
        {/* 1. Label block */}
        <div className="flex flex-col items-start gap-4">
          <div className="flex flex-col items-start">
            {isLens ? (
              <p className="text-[12px] font-semibold leading-[14px] text-clearlab-steel">
                {scheduleLabel}
              </p>
            ) : null}
            <p className="text-[16px] font-bold leading-8 tracking-[-0.025em] text-clearlab-dusk xl:w-[270px]">
              {productName}
            </p>
          </div>

          {isLens ? (
            <p className="flex flex-wrap items-start gap-x-3 text-[16px] font-normal leading-[18px]">
              {productChips.map((chip, idx) => (
                <Fragment key={chip}>
                  {idx > 0 ? <span className="text-clearlab-midnight">•</span> : null}
                  <span className="text-clearlab-midnight">{chip}</span>
                </Fragment>
              ))}
            </p>
          ) : null}
        </div>

        {/* 2. Body block — 80px from label for lens products (consistent across
            breakpoints), 16px for Eyedia. */}
        <div
          className={
            isLens
              ? 'mt-20 flex w-full flex-col items-start gap-14'
              : 'mt-4 flex w-full flex-col items-start gap-14'
          }
        >
          <p className="w-full text-[16px] font-normal leading-[44px] text-clearlab-midnight">
            {productDescription}
          </p>

          {inColorFamily ? (
            <Button
              variant="transparent"
              size="small"
              tone="blue"
              iconRight
              className="text-[16px]"
              onClick={(e) => {
                e.stopPropagation();
                setColorModalOpen(true);
              }}
            >
              {t('products.labels.viewColorRange')}
            </Button>
          ) : null}

          <div className="flex flex-col items-start gap-2">
            <p className="text-[12px] font-semibold leading-[14px] text-clearlab-steel">
              {t('products.labels.features')}
            </p>
            <ul className="block w-full list-disc space-y-5 ps-6 text-[16px] font-normal text-clearlab-midnight">
              {productFeatures.map((feature) => (
                <li key={feature}>
                  <span className="leading-8">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* 3. Specification block */}
        <div className="mt-10 w-full xl:mt-20">
          <SpecPanel
            label={specsLabel}
            specs={translatedSpecs}
            columns={specsColumns}
            cta={specsCta}
          />
        </div>
      </div>
      {showColorModal ? (
        <ColorModal isOpen={colorModalOpen} onClose={() => setColorModalOpen(false)} />
      ) : null}
    </article>
  );
}
